import { CurrencyDollarIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { filter, map, uniq } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import {
  selectBalanceDisplayInfoByCommodityWithBondsFiltered,
  selectCommodityThatHasEarnedInterest,
} from "@/redux/positions/positionsSelectors";
import type { RootState } from "@/redux/store";
import { useGetInterestAndTaxQuery } from "@/routes/_authenticated/yield/-api/yield";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import { EnumCurrencyType } from "@/shared/components/Common";
import { InterestEarnedChartContainer } from "@/shared/components/InterestEarnedChartContainer";
import MetalPageControls from "@/shared/components/MetalPageControls";
import type { AssetClass } from "@/shared/components/PageControls";
import { PortalTutorialButton } from "@/shared/components/PortalTutorialButton";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";

import BalanceOverviewCard from "./-components/BalanceOverviewCard";
import { PremiumSummaryCard } from "./-components/PremiumSummaryCard";

const BalanceSkeleton = () => (
  <div className="flex flex-col gap-4 md:gap-9">
    <Skeleton className="h-14 rounded md:h-[104px] md:rounded-2xl" />
    <Skeleton className="h-[614px] rounded-2xl" />
    <Skeleton className="h-[578px] rounded-2xl" />
  </div>
);

const BalanceEmptyState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <CurrencyDollarIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`You do not have any balances currently. To initiate a purchase of metal to fund your account, click here.`}</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Button color="primary" render={<Link to="/trade" />}>
          <CurrencyDollarIcon className="mr-2 h-6 w-auto" />
          {t`Purchase Metal`}
        </Button>
        <PortalTutorialButton />
      </div>
    </div>
  );
};

function BalanceView() {
  const eid = useSelector(selectMaybeActiveAccountEid);

  const assetClasses = [...Object.values(EnumCurrencyType)];
  const positionQuery = useGetAccountPositionQuery(eid || skipToken);
  const interestQuery = useGetInterestAndTaxQuery(eid || skipToken);

  const data = useSelector((state: RootState) =>
    selectBalanceDisplayInfoByCommodityWithBondsFiltered(state, assetClasses),
  );

  const dataWithoutUsd = filter(data, (balance) => balance.commodity !== EnumCurrencyType.Usd);
  const commoditiesWithInterest = useSelector(selectCommodityThatHasEarnedInterest);
  const commodities = useMemo(() => {
    const balanceCommodities = map(dataWithoutUsd, "commodity");
    const balanceOrInterestCommodities: AssetClass[] = uniq([...balanceCommodities, ...commoditiesWithInterest]);

    if (balanceOrInterestCommodities.length > 1) {
      balanceOrInterestCommodities.unshift("total");
    }

    return balanceOrInterestCommodities;
  }, [dataWithoutUsd, commoditiesWithInterest]);

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<BalanceSkeleton />}
      shouldRetry
      queries={[positionQuery, interestQuery]}
      translatedErrorMessage={t`We are having trouble loading your account details. Please try again later or contact our support team if the issue persists.`}
    >
      {dataWithoutUsd.length === 0 ? (
        <BalanceEmptyState />
      ) : (
        <div className="flex flex-col gap-4 md:gap-9">
          <MetalPageControls supportedAssets={commodities} />
          <BalanceOverviewCard balances={data} />
          <PremiumSummaryCard />
          <InterestEarnedChartContainer key={eid} />
        </div>
      )}
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/balance/")({
  component: BalanceView,
});
